import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  Copy,
  Trash2,
  Calendar,
  DollarSign,
  User,
  FileText,
  TrendingUp,
  BarChart3,
  MoreHorizontal
} from "lucide-react";
import { Link } from "react-router-dom";

// Mock data - substituir com dados reais da API
const mockBudgets = [
  {
    id: "1",
    number: "ORC-2024-001",
    client: "Empresa ABC Ltda",
    totalValue: 15000.00,
    status: "approved" as const,
    createdAt: "2024-01-15",
    validUntil: "2024-02-15",
    probability: 80,
    responsible: "João Silva"
  },
  {
    id: "2",
    number: "ORC-2024-002",
    client: "Serviços XYZ",
    totalValue: 8500.00,
    status: "pending" as const,
    createdAt: "2024-01-18",
    validUntil: "2024-02-18",
    probability: 60,
    responsible: "Maria Santos"
  },
  {
    id: "3",
    number: "ORC-2024-003",
    client: "Consultoria & Cia",
    totalValue: 25000.00,
    status: "sent" as const,
    createdAt: "2024-01-20",
    validUntil: "2024-02-20",
    probability: 70,
    responsible: "Pedro Costa"
  },
  {
    id: "4",
    number: "ORC-2024-004",
    client: "Tech Solutions",
    totalValue: 12000.00,
    status: "rejected" as const,
    createdAt: "2024-01-10",
    validUntil: "2024-02-10",
    probability: 0,
    responsible: "Ana Oliveira"
  }
];

const statusConfig = {
  draft: { label: "Rascunho", variant: "secondary" as const, color: "bg-muted0" },
  pending: { label: "Pendente", variant: "secondary" as const, color: "bg-yellow-500" },
  sent: { label: "Enviado", variant: "default" as const, color: "bg-blue-500" },
  approved: { label: "Aprovado", variant: "default" as const, color: "bg-green-500" },
  rejected: { label: "Rejeitado", variant: "destructive" as const, color: "bg-red-500" },
  expired: { label: "Expirado", variant: "destructive" as const, color: "bg-orange-500" }
};

export default function BudgetsListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    if (value) {
      searchParams.set("search", value);
    } else {
      searchParams.delete("search");
    }
    setSearchParams(searchParams);
  };

  const totalBudgets = mockBudgets.length;
  const totalValue = mockBudgets.reduce((sum, budget) => sum + budget.totalValue, 0);
  const approvedBudgets = mockBudgets.filter(b => b.status === "approved").length;
  const averageProbability = mockBudgets.reduce((sum, b) => sum + b.probability, 0) / totalBudgets;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Orçamentos</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalBudgets}</div>
            <p className="text-xs text-muted-foreground">
              +2 em relação ao mês passado
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Valor Total</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {totalValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </div>
            <p className="text-xs text-muted-foreground">
              +15% em relação ao mês passado
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taxa de Aprovação</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {((approvedBudgets / totalBudgets) * 100).toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              +5% em relação ao mês passado
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Prob. Média</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{averageProbability.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">
              Baseado em {totalBudgets} orçamentos
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar orçamentos..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-8"
          />
        </div>
        <Button variant="outline" size="sm">
          <Filter className="h-4 w-4 mr-2" />
          Filtros
        </Button>
        <Button variant="outline" size="sm">
          <Download className="h-4 w-4 mr-2" />
          Exportar
        </Button>
      </div>

      {/* Budgets Table */}
      <Card>
        <CardHeader>
          <CardTitle>Orçamentos</CardTitle>
          <CardDescription>
            Gerencie todos os seus orçamentos de serviços
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Número</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Valor</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Probabilidade</TableHead>
                <TableHead>Válido até</TableHead>
                <TableHead>Responsável</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mockBudgets.map((budget) => (
                <TableRow key={budget.id}>
                  <TableCell className="font-medium">
                    <Link
                      to={`/services/budgets/${budget.id}`}
                      className="hover:underline text-primary"
                    >
                      {budget.number}
                    </Link>
                  </TableCell>
                  <TableCell>{budget.client}</TableCell>
                  <TableCell>
                    {budget.totalValue.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    })}
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusConfig[budget.status].variant}>
                      {statusConfig[budget.status].label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-12 bg-border rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${budget.probability}%` }}
                        />
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {budget.probability}%
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>{budget.validUntil}</TableCell>
                  <TableCell>{budget.responsible}</TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <span className="sr-only">Abrir menu</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Ações</DropdownMenuLabel>
                        <DropdownMenuItem asChild>
                          <Link to={`/services/budgets/${budget.id}`}>
                            <Eye className="h-4 w-4 mr-2" />
                            Visualizar
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to={`/services/budgets/${budget.id}/edit`}>
                            <Edit className="h-4 w-4 mr-2" />
                            Editar
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to={`/services/budgets/${budget.id}/duplicate`}>
                            <Copy className="h-4 w-4 mr-2" />
                            Duplicar
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-red-600">
                          <Trash2 className="h-4 w-4 mr-2" />
                          Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}